import { PayPalButton } from './PayPalButton'
import type { PaymentFormHookState } from './usePaymentForm'
import type { PaymentFormProps } from './paymentFormTypes'

type PaymentPayPalPanelProps = Pick<
  PaymentFormProps,
  'onPayPalPaymentSuccess' | 'onPayPalPaymentError' | 'isLoading'
> & {
  form: PaymentFormHookState
}

export function PaymentPayPalPanel({
  form,
  onPayPalPaymentSuccess,
  onPayPalPaymentError,
  isLoading = false,
}: PaymentPayPalPanelProps) {
  const {
    paymentConfig,
    applicationData,
    paymentType,
    isContractPayment,
    paymentFee,
    productFee,
    totalAmount,
    paymentAmount,
  } = form

  if (!paymentConfig || !applicationData) return null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">PAYPAL</h2>
        <img
          src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_111x69.jpg"
          alt="PayPal"
          className="h-16 w-auto object-contain"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
          }}
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">
          {paymentType === 'APPLICATION_FEE'
            ? 'Liberty Rent - Application Fee'
            : paymentType === 'CONTRACT'
              ? 'Liberty Rent - Access Fee'
              : 'Liberty Rent - Payment'}
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-700">
              {paymentType === 'APPLICATION_FEE' ? 'Product Fee:' : 'Total Access Fee:'}
            </span>
            <span className="font-semibold text-gray-900">${(productFee || 0).toFixed(2)}</span>
          </div>
          {isContractPayment && paymentFee && (
            <div className="flex justify-between">
              <span className="text-gray-700">Service Fee:</span>
              <span className="font-semibold text-gray-900">
                ${paymentFee.processing_fee > 0 ? paymentFee.processing_fee.toFixed(2) : '0.00'}
              </span>
            </div>
          )}
          {!isContractPayment && (
            <div className="flex justify-between">
              <span className="text-gray-700">Service Fee:</span>
              <span className="font-semibold text-gray-900">Free</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-blue-200">
            <span className="font-semibold text-gray-900">Total Fee Paying:</span>
            <span className="font-bold text-lg text-gray-900">
              ${totalAmount !== null ? totalAmount.toFixed(2) : '0.00'}
            </span>
          </div>
        </div>
      </div>

      {productFee !== null && productFee > 0 && applicationData.applicant_id && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 flex justify-center">
          <PayPalButton
            paymentConfig={paymentConfig}
            applicationFee={productFee}
            paymentType={paymentType}
            paymentAmount={isContractPayment ? totalAmount : undefined}
            applicantId={applicationData.applicant_id}
            onPaymentSuccess={(paymentDetails) => {
              if (!onPayPalPaymentSuccess) return
              const details =
                isContractPayment && paymentAmount
                  ? { ...paymentDetails, paymentAmount: Number(paymentAmount) }
                  : paymentDetails
              onPayPalPaymentSuccess(details)
            }}
            onPaymentError={(error) => onPayPalPaymentError?.(error)}
            isLoading={isLoading}
          />
        </div>
      )}

      <div className="mt-6 space-y-2 text-sm text-gray-700">
        <p>
          Liberty Rent accepts PayPal for application fee payments. We will notify you once we
          have received your payment.
        </p>
        <p>
          <strong>Note:</strong> The PayPal account must be in the name of the Applicant to avoid
          potential fraud. You cannot use someone else&apos;s PayPal account.
        </p>
        <p>
          If you experience problems, contact our Customer Service on{' '}
          <a href="tel:8775817368" className="text-blue-600 hover:text-blue-800">
            877-581-7368
          </a>
        </p>
      </div>
    </div>
  )
}
